import { templateErrorRoute } from './routeTemplate';
import type { ErrorCollectorOptions, ErrorRecord, FailedCall } from './types';

/**
 * Most failures held for one report. Matches the cap the backend enforces on
 * POST /telemetry/errors, so a full buffer is always an acceptable batch.
 */
export const ERROR_BATCH_SIZE = 10;

/**
 * How often buffered failures are sent. This is the only trigger besides the
 * page being hidden: a broken login page that fails in a loop must send one
 * small batch per interval, not one request per failure.
 */
export const ERROR_FLUSH_INTERVAL_MS = 30_000;

/**
 * How many times one batch is sent before it is dropped. Only a network error
 * or a 5xx is tried again; a 4xx (404 flag off, 429 rate limited) is dropped at
 * once.
 */
const MAX_SEND_ATTEMPTS = 2;

/** A running error collector. */
export interface ErrorCollector {
  /** Offer a failed call. Kept only if the caller was not signed in. */
  record: (call: FailedCall) => void;
  /** Send whatever is buffered now. */
  flush: () => Promise<void>;
  /** Flush and stop the interval. */
  stop: () => void;
  /** Buffered records, for tests. */
  pending: () => ErrorRecord[];
}

/**
 * Create a collector for calls that failed before sign-in.
 *
 * Kept apart from the timing collector on purpose: these records are never
 * sampled, they go to a different endpoint, and that endpoint takes no
 * credentials. Like the timing collector it knows nothing about transport; the
 * host app supplies `send`.
 */
export function createErrorCollector(options: ErrorCollectorOptions): ErrorCollector {
  let buffer: ErrorRecord[] = [];
  let flushing = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  function record(call: FailedCall): void {
    // Decided now, not at flush. A failure seen while signed out is a pre-login
    // failure even if the user signs in before the next flush.
    if (!options.isEnabled()) {
      return;
    }

    // One bad record is dropped here. Sent, it would fail the server's check and
    // take the rest of the batch down with it.
    const entry = toErrorRecord(call);
    if (entry === null) {
      return;
    }

    buffer.push(entry);

    // Drop oldest: the latest failures are the ones the user is stuck on now.
    if (buffer.length > ERROR_BATCH_SIZE) {
      buffer = buffer.slice(-ERROR_BATCH_SIZE);
    }
  }

  async function flush(): Promise<void> {
    // A flush in flight owns the current buffer; a second would double-send it.
    if (flushing || buffer.length === 0) {
      return;
    }

    flushing = true;
    const batch = buffer;
    buffer = [];

    try {
      for (let attempt = 1; attempt <= MAX_SEND_ATTEMPTS; attempt += 1) {
        try {
          await options.send(batch);
          return;
        } catch (error) {
          // A 4xx (flag off, rate limited, a bad batch) will not get better on
          // an immediate retry, and retrying a 429 only makes it worse. Drop.
          //
          // Last attempt: drop the batch. The send is itself a call that may
          // fail for the same reason as the ones it reports.
          if (isClientError(error) || attempt === MAX_SEND_ATTEMPTS) {
            return;
          }
        }
      }
    } finally {
      flushing = false;
    }
  }

  function stop(): void {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
    void flush();
  }

  timer = setInterval(() => {
    void flush();
  }, ERROR_FLUSH_INTERVAL_MS);

  return { record, flush, stop, pending: () => [...buffer] };
}

/**
 * Whether a failed send carried an HTTP 4xx status. The host's `send` attaches
 * the status to the error it throws.
 */
function isClientError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/** The same route rule the server applies: a path of safe characters only. */
const ROUTE_CHARS = /^\/[A-Za-z0-9_:.\-/]*$/;

/** Longest route the server accepts. */
const MAX_ROUTE_LENGTH = 120;

/** The methods the server accepts. axios's 'request' fallback is not one. */
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Build the wire record, or null when the server would reject it.
 *
 * Only the templated route is sent. The raw path can hold an id (the legacy
 * email lookup puts the employee id in the path; the public /covid page puts a
 * health card number in it), and the caller is not signed in, so nothing that
 * could name a person leaves the browser. A route cut short for length ends in
 * a marker the server rejects, so it is dropped here rather than sent.
 */
function toErrorRecord(call: FailedCall): ErrorRecord | null {
  const route = templateErrorRoute(call.url);
  const method = call.method.toUpperCase();
  if (route.length > MAX_ROUTE_LENGTH || !ROUTE_CHARS.test(route) || !METHODS.includes(method)) {
    return null;
  }

  const record: ErrorRecord = {
    route,
    method,
    backend: call.backend,
    status: call.status,
    errorKind: call.errorKind,
    totalMs: Math.round(call.totalMs * 10) / 10,
  };

  if (call.traceId) {
    record.traceId = call.traceId;
  }

  return record;
}
